<script setup lang="ts">
import { curveMonotoneX, type ScaleLinear, line as d3line, type ScaleBand } from 'd3';
import type { LineChartData } from '../index.vue';

const props = defineProps<{
  lines: LineChartData['lines'];
  keys: string[];
  loading: boolean;
  yScale: ScaleLinear<number, number, never>;
  xScale: ScaleBand<string>;
}>();

// ================================================ Computed
const line = computed(() =>
  d3line<{ x: number; y: number }>()
    .x((d) => d.x)
    .y((d) => props.yScale(d.y))
    .curve(curveMonotoneX),
);

const lineData = (data: LineChartData['lines'][number]) => {
  return data['values']
    .map((value, index) => {
      const key = props.keys[index];
      if (!key) return null;
      return {
        x: (props.xScale(key) ?? 0) + props.xScale.bandwidth() / 2,
        y: value ?? 0,
        index,
      };
    })
    .filter((d) => d !== null && d.y !== undefined) as { x: number; y: number; index: number }[];
};

const linePaths = computed(() => {
  return props.lines.map((d) => {
    return { color: d.color, line: line.value(lineData(d)) ?? undefined };
  });
});
</script>
<template>
  <svg class="absolute inset-0 h-full w-full overflow-visible z-[20]" viewBox="0 0 100 100" preserveAspectRatio="none">
    <!-- Line -->
    <path
      v-for="(linePath, i) in linePaths"
      :key="i"
      :d="linePath.line"
      fill="none"
      :stroke="linePath.color"
      stroke-width="1"
      vector-effect="non-scaling-stroke"
    />
  </svg>
</template>
